/**
 * Web component: Video player (video-player-web)
 * -----------------------------------------------------------------------------
 * Video player.
 *
 */
import { vueReady } from '~/core/runners';
import classes from '~/config/classes';
import { debounce, enterFullscreen } from '~/helpers/general';
import useIndexStore from '~stores/index';

/**
 * DOM selectors.
 */
const selectors = {
  playButton: '[js-video-player="play"]',
  pauseButton: '[js-video-player="pause"]',
  muteButton: '[js-video-player="muted"]',
  unmuteButton: '[js-video-player="unmuted"]',
  video: '[js-video-player="video"]',
};

/**
 * Create new <video-player-web> custom element.
 */
class VideoPlayer extends HTMLElement {
  data(component) {
    return {
      selectors: {
        playButton: component.querySelector(selectors.playButton),
        pauseButton: component.querySelector(selectors.pauseButton),
        muteButton: component.querySelector(selectors.muteButton),
        unmuteButton: component.querySelector(selectors.unmuteButton),
        video: component.querySelector(selectors.video),
      },
      isMobile: true, // Default to mobile, will be updated when store is available
    };
  }

  /**
   * Component lifecycle - before mount.
   */
  constructor() {
    super();
    this.$data = {};
  }

  /**
   * Component lifecycle - mounted.
   */
  connectedCallback() {
    this.$data = this.data(this);
    this.$data.showFullscreen = this.getAttribute('show-fullscreen');

    // Try to get store and update isMobile
    try {
      const store = useIndexStore();
      this.$data.isMobile = store?.screen?.mobile ?? true;
    } catch (error) {
      // Store not available yet, keep default
    }

    this.addEventListener('click', this.handleVideo);

    /**
     * Add default listeners.
     */
    let throttleValue = 250;
    try {
      const store = useIndexStore();
      throttleValue = store?.settings?.throttle ?? 250;
    } catch (error) {
      // Store not available, use default
    }

    window.visualViewport.addEventListener(
      'resize',
      debounce(() => this.handleResize(), throttleValue),
      { passive: true },
    );
  }

  /**
   * Updates the isMobile value
   */
  handleResize() {
    try {
      const store = useIndexStore();
      this.$data.isMobile = store?.screen?.mobile ?? true;
    } catch (error) {
      // Store not available, keep current state
    }
  }

  /**
   * Copy promo code and toggle tooltip.
   * @param {Event} event - Click event.
   */
  async handleVideo(event) {
    const video = this.$data.selectors.video;
    const playButton = this.$data.selectors.playButton;

    if (event.target.matches(selectors.video)) {
      if (video.paused) {
        await video.play();

        if (
          this.$data.showFullscreen === 'always' ||
          (this.$data.showFullscreen === 'mobile' && this.$data.isMobile)
        ) {
          enterFullscreen(video);
        }

        if (video.muted) {
          this.showMuteButton();
        } else {
          this.showUnmuteButton();
        }

        playButton.classList.add(classes.hidden);
        return;
      }

      this.hideMuteButtons();
      video.pause();
      playButton.classList.remove(classes.hidden);
    }

    if (event.target.matches(selectors.muteButton)) {
      event.preventDefault();
      video.muted = false;
      this.showUnmuteButton();
      return;
    }

    if (event.target.matches(selectors.unmuteButton)) {
      event.preventDefault();
      video.muted = true;
      this.showMuteButton();
    }
  }

  /**
   * Hides Mute buttons
   */
  hideMuteButtons() {
    this.$data.selectors.unmuteButton.classList.add(classes.hidden);
    this.$data.selectors.muteButton.classList.add(classes.hidden);
  }

  /**
   * Shows Mute button
   */
  showMuteButton() {
    this.$data.selectors.muteButton.classList.remove(classes.hidden);
    this.$data.selectors.unmuteButton.classList.add(classes.hidden);
  }

  /**
   * Shows unmute button
   */
  showUnmuteButton() {
    this.$data.selectors.muteButton.classList.add(classes.hidden);
    this.$data.selectors.unmuteButton.classList.remove(classes.hidden);
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('video-player-web')) {
    return;
  }

  customElements.define('video-player-web', VideoPlayer);
});
